import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropdown", "list"]

  connect() {
    this.open = false
    this.loaded = false
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.boundHandleOutsideClick)
    this.subscribeToChannel()
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleOutsideClick)
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  subscribeToChannel() {
    if (!window.App || !window.App.cable) {
      // Retry after cable is ready
      setTimeout(() => this.subscribeToChannel(), 500)
      return
    }

    this.subscription = window.App.cable.subscriptions.create("ActivityLogChannel", {
      received: (data) => {
        this.handleNewLog(data)
      }
    })
  }

  handleNewLog(log) {
    // Show green pulse dot on bell
    this.showLiveDot()

    // Prepend to dropdown list if it's been loaded
    if (this.loaded) {
      const item = document.createElement("div")
      item.className = "bell-dropdown-item bell-dropdown-item-new"
      item.innerHTML = `
        <span class="bell-item-badge ${log.badge_class}">${log.action_label}</span>
        <span class="bell-item-desc">${this.escapeHtml(log.description)}</span>
        <span class="bell-item-time">${log.time_ago}</span>
      `

      // Remove empty message if present
      const empty = this.listTarget.querySelector(".bell-dropdown-empty")
      if (empty) empty.remove()

      this.listTarget.prepend(item)

      // Keep only 10 items
      const items = this.listTarget.querySelectorAll(".bell-dropdown-item")
      if (items.length > 10) {
        items[items.length - 1].remove()
      }
    }

    // Show toast notification
    this.showToast(log)
  }

  showLiveDot() {
    let dot = this.element.querySelector(".bell-live-dot")
    if (!dot) {
      dot = document.createElement("span")
      dot.className = "bell-live-dot"
      this.element.querySelector(".notification-bell").appendChild(dot)
    }
    dot.style.display = "block"
  }

  hideLiveDot() {
    const dot = this.element.querySelector(".bell-live-dot")
    if (dot) dot.style.display = "none"
  }

  showToast(log) {
    // Find or create toast container
    let container = document.querySelector(".toast-container")
    if (!container) {
      container = document.createElement("div")
      container.className = "toast-container"
      document.body.appendChild(container)
    }

    const toast = document.createElement("div")
    toast.className = "bell-toast"
    toast.innerHTML = `
      <span class="bell-item-badge ${log.badge_class}">${log.action_label}</span>
      <span class="bell-toast-desc">${this.escapeHtml(log.description)}</span>
    `
    container.appendChild(toast)

    // Auto-remove after 4 seconds
    setTimeout(() => {
      toast.classList.add("bell-toast-out")
      setTimeout(() => toast.remove(), 300)
    }, 4000)
  }

  toggle(event) {
    event.stopPropagation()
    this.open = !this.open
    this.dropdownTarget.style.display = this.open ? "block" : "none"

    if (this.open) {
      this.hideLiveDot()
      // Remove new-item highlights
      this.listTarget.querySelectorAll(".bell-dropdown-item-new").forEach(el => {
        el.classList.remove("bell-dropdown-item-new")
      })
      if (!this.loaded) {
        this.fetchRecent()
      }
    }
  }

  close() {
    this.open = false
    this.dropdownTarget.style.display = "none"
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  async fetchRecent() {
    try {
      const response = await fetch("/sp/log_history/recent", {
        headers: { "Accept": "application/json" }
      })
      const logs = await response.json()

      if (logs.length === 0) {
        this.listTarget.innerHTML = '<div class="bell-dropdown-empty">No recent activity</div>'
      } else {
        this.listTarget.innerHTML = logs.map(log => `
          <div class="bell-dropdown-item">
            <span class="bell-item-badge ${log.badge_class}">${log.action_label}</span>
            <span class="bell-item-desc">${this.escapeHtml(log.description)}</span>
            <span class="bell-item-time">${log.time_ago}</span>
          </div>
        `).join("")
      }

      this.loaded = true
    } catch (error) {
      console.error("Failed to load activity logs:", error)
      this.listTarget.innerHTML = '<div class="bell-dropdown-empty">Failed to load activity</div>'
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
